import {Component, Input, OnChanges, OnDestroy} from '@angular/core';
import {ContatosService} from '../../services/contatos.service';

/**
 * Perfil do cliente pelos pedidos anteriores nesta loja: canal habitual,
 * entrega/retirada, endereço, pagamento e último pedido, cada um com a
 * evidência ("usou este endereço em 8 dos últimos 10 pedidos"). Mesmo
 * cálculo que a Mia usa; aqui o atendente lê em dois segundos.
 * Entra no cadastro do cliente e, compacto, na tela de atendimento.
 */
@Component({
  selector: 'app-perfil-cliente',
  templateUrl: './perfil-cliente.component.html',
  styleUrls: ['./perfil-cliente.component.scss']
})
export class PerfilClienteComponent implements OnChanges, OnDestroy {
  /** Telefone completo (dígitos, com DDI) — a tela de conversas passa o wa_id. */
  @Input() telefone: string;
  /** Contato do cadastro (codigoPais + telefone) — alternativa ao telefone. */
  @Input() contato: any;
  /** Versão compacta para o painel da conversa. */
  @Input() compacto = false;

  /** Compacto começa recolhido (só a linha de chips); no cadastro fica sempre aberto. */
  expandido = true;

  perfil: any = null;
  carregando = false;
  semHistorico = false;
  erro = '';
  private sequencia = 0;
  private destruido = false;

  constructor(private contatos: ContatosService) {}

  ngOnChanges() {
    this.sequencia++;
    this.perfil = null;
    this.semHistorico = false;
    this.erro = '';
    this.expandido = !this.compacto;
    if (!this.telefoneCompleto) return;
    void this.carregue();
  }

  ngOnDestroy() { this.destruido = true; }

  get telefoneCompleto(): string {
    const bruto = String(this.telefone || '');
    // LID não é telefone: sem identificação não buscamos o perfil de outro contato
    if (bruto.includes('@')) return '';
    if (bruto) {
      const digitos = bruto.replace(/\D/g, '');
      return /^\d{10,15}$/.test(digitos) ? digitos : '';
    }
    const tel = String(this.contato?.telefone || '');
    if (!tel || tel.includes('@')) return '';
    const completo = `${this.contato?.codigoPais || '+55'}${tel}`.replace(/\D/g, '');
    return /^\d{10,15}$/.test(completo) ? completo : '';
  }

  async carregue() {
    const seq = ++this.sequencia;
    this.carregando = true;
    try {
      const perfil = await this.contatos.obtenhaPerfilCliente(this.telefoneCompleto);
      if (seq !== this.sequencia || this.destruido) return;
      this.perfil = perfil || null;
      this.semHistorico = !perfil;
    } catch (erro: any) {
      if (seq !== this.sequencia || this.destruido) return;
      this.erro = typeof erro === 'string' ? erro : 'Não foi possível carregar o perfil do cliente.';
    } finally {
      if (seq === this.sequencia) this.carregando = false;
    }
  }

  get podeRecolher(): boolean { return this.compacto && !!this.perfil; }

  alterne() { this.expandido = !this.expandido; }

  /**
   * Hábitos prontos para a tela: rótulo, ícone conforme o valor (moto para entrega,
   * loja para retirada, QR para pix…), valor completo, versão curta para os chips
   * do estado recolhido e a evidência. Só entram os que o perfil tem.
   */
  get fatos(): Array<{rotulo: string; icone: string; valor: string; curto: string; evidencia?: string; largo?: boolean}> {
    const p = this.perfil;
    if (!p) return [];
    const fatos = [];
    if (this.canal) {
      const icones = {LINK: 'mdi-link-variant', CONVERSA: 'mdi-robot', OUTRO: 'mdi-cellphone-message'};
      const curtos = {LINK: 'Pelo link', CONVERSA: 'Pela Mia', OUTRO: 'Outros canais'};
      fatos.push({rotulo: 'Como pede', icone: icones[p.canal.habitual], valor: this.canal, curto: curtos[p.canal.habitual], evidencia: p.canal.evidencia});
    }
    if (this.recebimento) {
      const icones = {ENTREGA: 'mdi-truck-delivery', RETIRADA: 'mdi-store', LOCAL: 'mdi-silverware-fork-knife'};
      const curtos = {ENTREGA: 'Entrega', RETIRADA: 'Retira', LOCAL: 'No local'};
      fatos.push({rotulo: 'Recebimento', icone: icones[p.entrega.habitual], valor: this.recebimento, curto: curtos[p.entrega.habitual], evidencia: p.entrega.evidencia});
    }
    if (p.pagamento?.habitual) {
      const desc = String(p.pagamento.habitual.descricao || '');
      const chave = desc.toLowerCase();
      const icone = chave.includes('pix') ? 'mdi-qrcode' : chave.includes('dinheiro') ? 'mdi-cash' : 'mdi-credit-card';
      fatos.push({rotulo: 'Pagamento', icone, valor: desc, curto: desc, evidencia: p.pagamento.evidencia});
    }
    if (p.endereco?.habitual) {
      const desc = String(p.endereco.habitual.descricao || '');
      // no chip fica só o bairro (último trecho), o endereço inteiro vai no tooltip
      const partes = desc.split('·').map(t => t.trim()).filter(Boolean);
      fatos.push({rotulo: 'Endereço', icone: 'mdi-map-marker-outline', valor: desc, curto: partes[partes.length - 1] || desc, evidencia: p.endereco.evidencia, largo: true});
    }
    if (p.ultimoPedido) {
      const total = new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(Number(p.ultimoPedido.total) || 0);
      fatos.push({rotulo: 'Último pedido', icone: 'mdi-history', valor: `${this.quandoFoiOUltimo} · ${p.ultimoPedido.status} · ${total}`,
        curto: `${this.quandoFoiOUltimo.toLowerCase()} · ${total}`, evidencia: p.ultimoPedido.resumo, largo: true});
    }
    return fatos;
  }

  get canal(): string {
    switch (this.perfil?.canal?.habitual) {
      case 'LINK': return 'Pede pelo link do cardápio';
      case 'CONVERSA': return 'Pede pela conversa com a Mia';
      case 'OUTRO': return 'Pede por outros canais';
      default: return '';
    }
  }

  get recebimento(): string {
    switch (this.perfil?.entrega?.habitual) {
      case 'ENTREGA': return 'Recebe em casa';
      case 'RETIRADA': return 'Retira na loja';
      case 'LOCAL': return 'Consome no local';
      default: return '';
    }
  }

  get quandoFoiOUltimo(): string {
    const dias = Number(this.perfil?.ultimoPedido?.feitoHaDias);
    if (!Number.isFinite(dias)) return '';
    if (dias === 0) return 'Hoje';
    if (dias === 1) return 'Ontem';
    return `Há ${dias} dias`;
  }

  get clienteDesde(): string {
    const data = this.perfil?.primeiroPedidoEm ? new Date(this.perfil.primeiroPedidoEm) : null;
    if (!data || isNaN(data.getTime())) return '';
    return data.toLocaleDateString('pt-BR', {month: 'short', year: 'numeric'}).replace('.', '');
  }
}
